import { useEffect } from 'react';
import { Table, Button, Icon } from '@getsynapse/design-system';
import moment from 'moment';
import intl from 'react-intl-universal';
import { getOwnersCell, getProgramStatusPill } from './helpers';
import { Link, useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector, useDispatch } from 'react-redux';
import {
  Program,
  SortingType,
  PilotPagesToTrack,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';
import { DATE, PROGRAM_FIELDS, PATHS, SLICE_STATUS } from 'utils/constants';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import {
  selectPaginatedPrograms,
  setProgramsSortingOptions,
  programsSearch,
  selectProgramsSliceStatus,
} from 'state/Programs/programsSlice';
import {
  getProgramPermissions,
  programPermissionsSelectors,
} from 'state/normalized/programPermissions/programPermissionsSlice';
import { useAppThunkDispatch } from 'state/store';
import emptyProgramsTable from 'assets/images/programs-table-empty.svg';
import noSearchResults from 'assets/images/no-projects-found-table.svg';
import noFilterResults from 'assets/images/empty-filtered-projects-table.svg';
import isEmpty from 'lodash/isEmpty';
import TableEmptyErrorState from 'Molecules/TableEmptyErrorState/TableEmptyErrorState';
import RenderNoRecords from 'Atoms/NoRecords/NoRecords';
import { formatProgramNumber } from 'utils/formatters';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import { useUpdateProgramStatus } from '../hooks/useUpdateProgramStatus';
import { chunkArray } from 'Pages/helpers';

interface ProgramsTableProps {
  onAddProgram: () => void;
  areFiltersApplied?: boolean;
}

const ProgramsTable: React.FC<ProgramsTableProps> = ({
  onAddProgram,
  areFiltersApplied = false,
}) => {
  const { inlineEditingProgramStatusProgram = false } = useFlags();
  const dispatch = useDispatch();
  const thunkDispatch = useAppThunkDispatch();
  const programs = useSelector(selectPaginatedPrograms);
  const sliceStatus = useSelector(selectProgramsSliceStatus);
  const searchValue = useSelector(programsSearch);
  const getPermissionsById = useSelector(
    programPermissionsSelectors.selectProgramPermissionsById
  );

  const { handleSaveProgramStatus, getProgramStatuses } =
    useUpdateProgramStatus();

  useEffect(() => {
    if (programs.length === 0) return;

    const programIds = programs.map((program) => program.id);
    const uniqueProgramIds = [...new Set(programIds)];
    const chunks = chunkArray(uniqueProgramIds, DEFAULT_PAGE_SIZE);

    chunks.forEach((chunk) => {
      thunkDispatch(getProgramPermissions({ programIds: chunk }));
    });
  }, [programs, thunkDispatch]);

  const handleSort = (orderByParam: string, order: SortingType) => {
    dispatch(setProgramsSortingOptions(orderByParam, order));
  };
  const history = useHistory();

  return (
    <div className='max-h-programs-table-content overflow-auto w-full'>
      <Table
        className='w-full mb-5 relative'
        canSelectRows={false}
        isSelectRowCellSticky
        data-cy='programs-table-body'
        data={{
          headData: {
            stickyHeader: true,
            handleSort,
            rowProps: {
              className: 'w-full table-fixed h-auto min-h-10 z-2',
            },
            headCells: [
              {
                columnName: PROGRAM_FIELDS.PROGRAM_NUMBER,
                content: intl.get(
                  'PROGRAMS_LIST_PAGE.TABLE.COLUMNS.PROGRAM_NUMBER'
                ),
                className: 'font-semibold w-20 py-3',
                'data-cy': 'header__program-number',
              },
              {
                columnName: PROGRAM_FIELDS.TITLE,
                content: intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.TITLE'),
                className: 'font-semibold max-w-94 py-3',
                'data-cy': 'header__program-title',
              },
              {
                columnName: PROGRAM_FIELDS.STATUS,
                content: intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.STATUS'),
                className: 'font-semibold w-1/12 py-3',
                'data-cy': 'header__program-status',
              },
              {
                columnName: PROGRAM_FIELDS.OWNERS,
                content: intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.OWNERS'),
                className: 'font-semibold w-1/6 py-3',
                'data-cy': 'header__program-owners',
              },
              {
                columnName: PROGRAM_FIELDS.DELIVERY,
                content: intl.get(
                  'PROGRAMS_LIST_PAGE.TABLE.COLUMNS.DELIVERY_TYPE'
                ),
                className: 'font-semibold w-1/6 py-3',
                'data-cy': 'header__program-delivery',
              },
              {
                columnName: PROGRAM_FIELDS.CREATION,
                content: intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.CREATION'),
                className: 'font-semibold w-1/12 py-3',
                'data-cy': 'header__program-creation',
              },
            ],
          },
          rows: programs.map((program: Program) => {
            const permissions = getPermissionsById(program.id);
            const canEdit = permissions?.canEditStatusInline ?? false;
            return {
              id: program.id,
              onClick: inlineEditingProgramStatusProgram
                ? undefined
                : () => history.push(`${PATHS.PROGRAM_PAGE}/${program.id}`),
              className: `${
                inlineEditingProgramStatusProgram ? '' : 'cursor-pointer '
              }w-full table-fixed`,
              cells: [
                {
                  content: formatProgramNumber(program.program_number),
                  className: 'w-20',
                },
                {
                  content: (
                    <div className='flex items-center gap-2'>
                      {program.is_archived && (
                        <Icon
                          data-cy={`archive-icon-${program.id}`}
                          name='archive'
                          className='text-neutral-dark'
                          size='sm'
                          title={intl.get(
                            'PROGRAMS_LIST_PAGE.APPLIED_FILTERS.ARCHIVED'
                          )}
                        />
                      )}
                      <Link
                        to={`${PATHS.PROGRAM_PAGE}/${program.id}`}
                        title={program.title}
                        className={
                          inlineEditingProgramStatusProgram
                            ? 'truncate cursor-pointer hover:underline hover:text-steel-blue hover:font-semibold'
                            : 'truncate'
                        }
                        onClick={
                          !inlineEditingProgramStatusProgram
                            ? (event: React.MouseEvent) =>
                                event.stopPropagation()
                            : undefined
                        }
                      >
                        {program.title}
                      </Link>
                    </div>
                  ),
                  className: 'max-w-94 truncate',
                },
                {
                  content: inlineEditingProgramStatusProgram ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <StatusTableRowCell
                        value={program.status}
                        customizedValue={(status) =>
                          getProgramStatusPill(status, canEdit)
                        }
                        options={getProgramStatuses(program.programProjects)}
                        isEditable={canEdit}
                        className='w-full'
                        isTaskProjectDisabled={false}
                        canInlineEditTask={true}
                        onSave={(newValue) => {
                          handleSaveProgramStatus(
                            newValue,
                            program.id,
                            program.status
                          );
                        }}
                        canEdit={canEdit}
                        entityType={StatusInlineEditingEntityType.PROGRAM}
                        userPilotEventOrigin={PilotPagesToTrack.PROGRAM_LIST}
                      />
                    </div>
                  ) : (
                    getProgramStatusPill(program.status)
                  ),
                  className: `w-1/12${
                    inlineEditingProgramStatusProgram ? ' p-0' : ''
                  }`,
                },
                {
                  content: getOwnersCell(program.programOwners),
                  className: 'truncate w-1/6',
                },
                {
                  content: program.delivery_type
                    ? intl.get(
                        `PROGRAMS_LIST_PAGE.DELIVERY_TYPES.${program.delivery_type?.toUpperCase()}`
                      )
                    : '-',
                  className: 'truncate w-1/6',
                },
                {
                  content: moment(new Date(program.createdAt)).format(
                    DATE.SHORT_FORMAT
                  ),
                  className: 'truncate w-1/12',
                },
              ],
            };
          }),
        }}
        emptyComponent={
          sliceStatus === SLICE_STATUS.FAILED ? (
            <TableEmptyErrorState />
          ) : !isEmpty(searchValue) || !areFiltersApplied ? (
            <RenderNoRecords
              imageSrc={
                !isEmpty(searchValue) ? noSearchResults : noFilterResults
              }
              className='h-empty-table-body'
              caption={intl.get('PROGRAMS_LIST_PAGE.TABLE.NO_RECORDS')}
            />
          ) : (
            <RenderNoRecords
              imageSrc={emptyProgramsTable}
              className='h-empty-table-body'
              caption={intl.get('PROGRAMS_LIST_PAGE.TABLE.EMPTY')}
              labelClassName='mt-0'
            >
              <Button className='mt-2 mx-auto' onClick={onAddProgram}>
                {intl.get('PROGRAMS_LIST_PAGE.TABLE.GET_STARTED')}
              </Button>
            </RenderNoRecords>
          )
        }
      />
    </div>
  );
};

export default ProgramsTable;
